import { useEffect, useMemo, useRef, useState } from 'react';
import { Search, Check, X, Dumbbell } from 'lucide-react';
import type { AudienceType, Workout } from '@techsofit/api-types';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Input, Select, Modal, Badge } from '@techsofit/ui';
import { useAsync } from '../lib/useAsync';
import { createAssignment, getWorkouts, getWorkoutGroups, listMembers } from '../lib/gymApi';

function isoToday(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

interface ScheduleFormProps {
  /** Day to default the date field to. Falls back to today. */
  date?: string;
  /** Pre-select a workout so the picker starts with it chosen (e.g. scheduling from a card). */
  workoutId?: string;
  onClose: () => void;
  onSaved: () => void;
}

/** Place one or more workouts on the board — for a member, a group, or everyone. */
export function ScheduleForm({ date, workoutId: initialWorkoutId, onClose, onSaved }: ScheduleFormProps) {
  const { data: workouts } = useAsync(getWorkouts);
  const { data: groups } = useAsync(getWorkoutGroups);
  const { data: membersPage } = useAsync(() => listMembers({ size: 1000 }));
  const members = membersPage?.items ?? [];

  const [workoutIds, setWorkoutIds] = useState<string[]>(initialWorkoutId ? [initialWorkoutId] : []);
  const [scheduledDate, setScheduledDate] = useState(date ?? isoToday());
  const [audienceType, setAudienceType] = useState<AudienceType>('EVERYONE');
  const [audienceId, setAudienceId] = useState('');
  const [timeLabel, setTimeLabel] = useState('');
  const [openSession, setOpenSession] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save() {
    setError(null);
    if (workoutIds.length === 0) {
      setError('Pick at least one workout.');
      return;
    }
    if (audienceType !== 'EVERYONE' && !audienceId) {
      setError(audienceType === 'MEMBER' ? 'Pick a member.' : 'Pick a group.');
      return;
    }
    setSaving(true);
    try {
      await Promise.all(
        workoutIds.map((workoutId) =>
          createAssignment({
            workoutId,
            scheduledDate,
            timeLabel: timeLabel.trim() || null,
            audienceType,
            audienceId: audienceType === 'EVERYONE' ? null : audienceId,
            openSession,
          }),
        ),
      );
      onSaved();
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not schedule.');
      setSaving(false);
    }
  }

  return (
    <Modal open onClose={onClose}>
      <div className="flex max-h-[85vh] w-full flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-ink">Schedule workouts</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="text-muted hover:text-ink">
            <X className="size-5" aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto pr-0.5">
          <div className="flex flex-col gap-4">
            <WorkoutPicker
              workouts={workouts ?? []}
              loading={!workouts}
              selectedIds={workoutIds}
              onAdd={(id) => setWorkoutIds((prev) => [...prev, id])}
              onRemove={(id) => setWorkoutIds((prev) => prev.filter((x) => x !== id))}
            />

            <Input label="Date" type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} />

            <Select
              label="Who"
              value={audienceType}
              onChange={(e) => {
                setAudienceType(e.target.value as AudienceType);
                setAudienceId('');
              }}
            >
              <option value="EVERYONE">Everyone</option>
              <option value="MEMBER">A member</option>
              <option value="GROUP">A group</option>
            </Select>

            {audienceType === 'MEMBER' && (
              <Select label="Member" value={audienceId} onChange={(e) => setAudienceId(e.target.value)}>
                <option value="">Pick a member…</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.fullName}
                  </option>
                ))}
              </Select>
            )}
            {audienceType === 'GROUP' && (
              <Select label="Group" value={audienceId} onChange={(e) => setAudienceId(e.target.value)}>
                <option value="">Pick a group…</option>
                {(groups ?? []).map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </Select>
            )}

            <Input label="Time (optional)" value={timeLabel} onChange={(e) => setTimeLabel(e.target.value)} placeholder="e.g. 6pm" />

            <label className="flex cursor-pointer items-center gap-2.5 text-sm font-medium text-ink-soft">
              <input type="checkbox" checked={openSession} onChange={(e) => setOpenSession(e.target.checked)} className="size-4 rounded border-line accent-ink" />
              Open session — anyone can join
            </label>

            {error && (
              <p role="alert" className="rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">
                {error}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-line pt-3">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={save} loading={saving} disabled={workoutIds.length === 0}>
            {!saving && <Check className="size-4" aria-hidden />} Schedule{workoutIds.length > 1 ? ` ${workoutIds.length}` : ''}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Multi-select combobox: chips for chosen workouts, results in an overlay so the form doesn't shift. */
function WorkoutPicker({
  workouts,
  loading,
  selectedIds,
  onAdd,
  onRemove,
}: {
  workouts: Workout[];
  loading: boolean;
  selectedIds: string[];
  onAdd: (id: string) => void;
  onRemove: (id: string) => void;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close the dropdown when clicking outside the picker.
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const selected = selectedIds.map((id) => workouts.find((w) => w.id === id)).filter(Boolean) as Workout[];
  const matches = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return workouts.filter((w) => !selectedIds.includes(w.id) && (!needle || w.name.toLowerCase().includes(needle))).slice(0, 50);
  }, [workouts, selectedIds, q]);

  return (
    <div ref={ref}>
      <p className="mb-1.5 text-sm font-medium text-ink-soft">
        Workouts{selected.length > 0 && <span className="text-muted"> · {selected.length} selected</span>}
      </p>

      {selected.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {selected.map((w) => (
            <span key={w.id} className="inline-flex items-center gap-1.5 rounded-pill bg-surface py-1 pl-3 pr-1.5 text-sm font-medium text-ink">
              {w.name}
              <button type="button" aria-label={`Remove ${w.name}`} onClick={() => onRemove(w.id)} className="grid size-5 place-items-center rounded-full text-muted hover:bg-line hover:text-ink">
                <X className="size-3.5" aria-hidden />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden />
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={selected.length > 0 ? 'Add another workout…' : 'Search workouts…'}
          className="h-touch w-full rounded-control border border-line bg-elevated pl-9 pr-3 text-sm text-ink outline-none focus:border-ink"
        />

        {open && (
          <div className="absolute left-0 right-0 top-full z-20 mt-1 max-h-64 overflow-y-auto rounded-control border border-line bg-card shadow-card-hover">
            {loading ? (
              <p className="py-6 text-center text-sm text-muted">Loading…</p>
            ) : matches.length === 0 ? (
              <p className="py-6 text-center text-sm text-muted">
                {workouts.length === 0 ? 'No workouts yet. Build one first.' : q.trim() ? 'No matches.' : 'All workouts added.'}
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-line">
                {matches.map((w) => {
                  const groups = Array.from(new Set(w.items.flatMap((i) => i.muscleGroups))).slice(0, 3);
                  return (
                    <li key={w.id}>
                      <button
                        type="button"
                        onClick={() => {
                          onAdd(w.id);
                          setQ('');
                        }}
                        className="flex w-full items-center gap-3 p-3 text-left hover:bg-surface"
                      >
                        <span className="grid size-10 shrink-0 place-items-center rounded-control bg-surface text-muted">
                          <Dumbbell className="size-5" aria-hidden />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-ink">{w.name}</span>
                          <span className="mt-0.5 flex flex-wrap items-center gap-1">
                            <span className="text-xs text-muted">
                              {w.itemCount} {w.itemCount === 1 ? 'exercise' : 'exercises'}
                            </span>
                            {groups.map((g) => (
                              <Badge key={g}>{g}</Badge>
                            ))}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
